import React, { useState } from "react";
import {
  Card, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography, Avatar, Box, Paper,
  TextField, MenuItem, TablePagination, useMediaQuery,
  Chip, Stack
} from "@mui/material";
import { Link } from "react-router-dom";
import VerifiedIcon from "@mui/icons-material/Verified";
import CancelIcon from "@mui/icons-material/Cancel";

// Custom COLORS for a consistent aesthetic
const MS_COLORS = {
  primary: '#2D4A3D',
  primaryLight: '#EBF5EE',
  text: '#323130',
  textSecondary: '#605D5C',
  background: '#FFFFFF',
  backgroundLight: '#F3F2F1',
  border: '#E1E1E1',
  hoverBackground: '#F0F0F0',
  white: '#FFFFFF',
  accent: '#107C10',
  red: '#D83B01',
};

// Component now receives `organizations` as a prop
const OrganizationManagement = ({ organizations }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [verificationFilter, setVerificationFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const isSmallScreen = useMediaQuery("(max-width:600px)");

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredOrganizations = organizations.filter((org) => {
    const matchesSearch =
      org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      org.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesVerification =
      verificationFilter === "all" ||
      org.verified === (verificationFilter === "yes" ? 1 : 0);

    return matchesSearch && matchesVerification;
  });

  return (
    <Card
      sx={{
        backgroundColor: "#fff",
        borderRadius: "16px",
        boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
        border: "1px solid rgba(0,0,0,0.05)",
        p: 3,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: isSmallScreen ? "column" : "row",
          gap: 2,
          mb: 3,
          alignItems: "center",
          backgroundColor: MS_COLORS.backgroundLight,
          p: isSmallScreen ? 1.5 : 2,
          borderRadius: 1,
          border: `1px solid ${MS_COLORS.border}`,
        }}
      >
        <TextField
          placeholder="Search by name or email"
          variant="outlined"
          size="small"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            flex: 1,
            "& .MuiOutlinedInput-root": {
              borderRadius: "4px",
              padding: isSmallScreen ? "6px 8px" : "8px 12px",
              backgroundColor: MS_COLORS.white,
            },
            "& .MuiInputBase-input": {
              fontSize: isSmallScreen ? "0.8rem" : "0.9rem",
              color: MS_COLORS.text,
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: MS_COLORS.border,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: MS_COLORS.primary,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: MS_COLORS.primary,
              borderWidth: 2,
            },
          }}
        />

        <TextField
          select
          variant="outlined"
          size="small"
          value={verificationFilter}
          onChange={(e) => setVerificationFilter(e.target.value)}
          sx={{
            minWidth: isSmallScreen ? "100%" : 200,
            "& .MuiOutlinedInput-root": {
              borderRadius: "4px",
              padding: isSmallScreen ? "6px 8px" : "8px 12px",
              backgroundColor: MS_COLORS.white,
            },
            "& .MuiInputBase-input": {
              fontSize: isSmallScreen ? "0.8rem" : "0.9rem",
              color: MS_COLORS.text,
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: MS_COLORS.border,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: MS_COLORS.primary,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: MS_COLORS.primary,
              borderWidth: 2,
            },
          }}
        >
          <MenuItem value="all" sx={{ fontSize: '0.9rem' }}>All Organizations</MenuItem>
          <MenuItem value="yes" sx={{ fontSize: '0.9rem' }}>Verified</MenuItem>
          <MenuItem value="no" sx={{ fontSize: '0.9rem' }}>Unverified</MenuItem>
        </TextField>
      </Box>

      {filteredOrganizations.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            color: MS_COLORS.textSecondary,
            mt: 4,
            mb: 4,
            fontSize: "0.9rem",
            p: 3,
            border: `1px dashed ${MS_COLORS.border}`,
            borderRadius: 1,
            bgcolor: MS_COLORS.backgroundLight,
          }}
        >
          <Typography variant="body1">
            No organizations found matching your criteria.
          </Typography>
        </Box>
      ) : (
        <>
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 1,
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            overflowX: "auto",
            border: `1px solid ${MS_COLORS.border}`,
          }}
        >
          <Table sx={{ minWidth: 800 }}>
            <TableHead sx={{ bgcolor: MS_COLORS.backgroundLight }}>
              <TableRow>
                {[
                  "ID",
                  "Organization",
                  "Email",
                  !isSmallScreen && "Phone",
                  "Verified",
                  "Raised",
                  "Withdrawn",
                  "Balance",
                  "Joined",
                  "Actions",
                ].map(
                  (head, index) =>
                    head && (
                      <TableCell
                        key={index}
                        sx={{
                          fontWeight: "bold",
                          fontSize: "0.85rem",
                          whiteSpace: "nowrap",
                          color: MS_COLORS.text,
                        }}
                      >
                        {head}
                      </TableCell>
                    )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrganizations
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((org, index) => (
                  <TableRow
                    key={org.id}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      "&:hover": {
                        backgroundColor: MS_COLORS.hoverBackground,
                      },
                    }}
                  >
                    <TableCell sx={{ fontSize: "0.8rem", color: MS_COLORS.textSecondary }}>
                      {page * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar
                          src={org.logo_url || "/default-org-logo.png"}
                          alt={org.name}
                          sx={{ width: 32, height: 32 }}
                        />
                        <Typography
                          fontWeight="bold"
                          sx={{ fontSize: "0.8rem", color: MS_COLORS.text }}
                        >
                          {org.name}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", color: MS_COLORS.textSecondary }}>
                      {org.email}
                    </TableCell>
                    {!isSmallScreen && (
                      <TableCell sx={{ fontSize: "0.8rem", color: MS_COLORS.textSecondary }}>
                        {org.phone || "N/A"}
                      </TableCell>
                    )}
                    <TableCell>
                      <Chip
                        icon={org.verified ? <VerifiedIcon /> : <CancelIcon />}
                        label={org.verified ? "VERIFIED" : "UNVERIFIED"}
                        size="small"
                        sx={{
                          fontWeight: "bold",
                          fontSize: "0.7rem",
                          color: MS_COLORS.white,
                          backgroundColor: org.verified ? MS_COLORS.accent : MS_COLORS.red,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", whiteSpace: "nowrap", color: MS_COLORS.textSecondary }}>
                      UGX {org.raised?.toLocaleString("en-UG") || "0"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", whiteSpace: "nowrap", color: MS_COLORS.textSecondary }}>
                      UGX {org.total_withdrawn?.toLocaleString("en-UG") || "0"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", whiteSpace: "nowrap", color: MS_COLORS.textSecondary }}>
                      UGX {org.balance?.toLocaleString("en-UG") || "0"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", whiteSpace: "nowrap", color: MS_COLORS.textSecondary }}>
                      {formatDate(org.created_at)}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                      <Link
                        to={`/admin/organization-details/${org.slug}`}
                        style={{
                          textDecoration: "none",
                          color: MS_COLORS.primary,
                          fontWeight: "bold",
                          fontSize: "0.8rem",
                        }}
                      >
                        Manage Org
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        {filteredOrganizations.length > 0 && (
          <TablePagination
            component="div"
            count={filteredOrganizations.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
            sx={{
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select, & .MuiTablePagination-actions":
              {
                fontSize: "0.8rem",
                color: MS_COLORS.textSecondary,
              },
            }}
          />
        )}
      </>
      )}
    </Card>
  );
};

export default OrganizationManagement;